"use client"

import { useState, useEffect } from "react"
import { FaCheckCircle, FaTimesCircle, FaSpinner, FaExternalLinkAlt } from "react-icons/fa"
import Sidebar from "../components/Sidebar.js"
import { getSubmittedChallenges, approveRequest, rejectRequest } from "../comman/Challenges.js"
import { useParams } from "react-router-dom"

const ChallengeApprovalPage = () => {
  const [challenges, setChallenges] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const { challengeId } = useParams()

  const fetchChallenges = async () => {
    try {
      setLoading(true)
      const response = await getSubmittedChallenges(challengeId)
      console.log("API Response:", response)
      setChallenges(response.data || [])
      setError(null)
    } catch (error) {
      console.error("Error fetching challenges:", error)
      setError("Failed to load challenges. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (challengeId) {
      fetchChallenges()
    }
  }, [challengeId]) // Added challengeId as a dependency

  const handleApprove = async (studentId, challengeId) => {
    try {
      await approveRequest(studentId, challengeId)
      setChallenges((prevChallenges) =>
        prevChallenges.map((challenge) =>
          challenge.student._id === studentId && challenge.challenge._id === challengeId
            ? { ...challenge, status: "success" }
            : challenge,
        ),
      )
      fetchChallenges()
    } catch (error) {
      console.error("Error approving challenge:", error)
      setError("Failed to approve challenge. Please try again.")
    }
  }

  const handleReject = async (studentId, challengeId) => {
    try {
      await rejectRequest(studentId, challengeId)
      setChallenges((prevChallenges) =>
        prevChallenges.map((challenge) =>
          challenge.student._id === studentId && challenge.challenge._id === challengeId
            ? { ...challenge, status: "rejected" }
            : challenge,
        ),
      )
      fetchChallenges()
    } catch (error) {
      console.error("Error rejecting challenge:", error)
      setError("Failed to reject challenge. Please try again.")
    }
  }

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 p-4 md:p-6 md:ml-64 w-full max-w-[100vw] overflow-hidden">
        <div className="max-w-full overflow-hidden">
          <h1 className="text-2xl md:text-3xl font-bold text-[#12143F] mb-6">Challenge Approval</h1>
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
              <strong className="font-bold">Error!</strong>
              <span className="block sm:inline"> {error}</span>
            </div>
          )}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <FaSpinner className="animate-spin text-4xl text-[#12143F]" />
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-md p-4 md:p-6 overflow-hidden">
              <div className="overflow-x-auto -mx-4 md:-mx-6">
                <div className="inline-block min-w-full align-middle p-4 md:p-6">
                  <div className="overflow-hidden border rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-[#12143F]">
                        <tr>
                          <th
                            scope="col"
                            className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                          >
                            Student Name
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                          >
                            Challenge Name
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                          >
                            Category
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                          >
                            Video
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                          >
                            Level
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                          >
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {Array.isArray(challenges) && challenges.length > 0 ? (
                          challenges.map((challenge) => (
                            <tr key={challenge._id} className="hover:bg-gray-50 transition duration-150">
                              <td className="px-4 py-4 text-sm text-gray-900 max-w-[200px] truncate">
                                {challenge?.student?.profile?.name}
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-900 max-w-[200px] truncate">
                                {challenge?.challenge?.name}
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-900 max-w-[150px] truncate">
                                {challenge?.challenge?.category}
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-900">
                                <a
                                  href={challenge?.videoLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-500 hover:text-blue-700 transition-colors duration-200 flex items-center"
                                >
                                  View Video
                                  <FaExternalLinkAlt className="ml-1" size={12} />
                                </a>
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-900 whitespace-nowrap">
                                {challenge?.challenge?.level || "N/A"}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm">
                                {challenge.status === "success" ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    <FaCheckCircle className="mr-1" />
                                    Approved
                                  </span>
                                ) : challenge.status === "rejected" ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                    <FaTimesCircle className="mr-1" />
                                    Rejected
                                  </span>
                                ) : (
                                  <div className="flex items-center space-x-2">
                                    <button
                                      onClick={() => handleApprove(challenge.student._id, challenge.challenge._id)}
                                      className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition duration-150"
                                    >
                                      <FaCheckCircle className="mr-1" />
                                      Approve
                                    </button>
                                    <button
                                      onClick={() => handleReject(challenge.student._id, challenge.challenge._id)}
                                      className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition duration-150"
                                    >
                                      <FaTimesCircle className="mr-1" />
                                      Reject
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="6" className="px-4 py-4 text-sm text-gray-500 text-center">
                              No challenges found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ChallengeApprovalPage

